/**
 * Color tokens.
 *
 * Components consume the exported `color` object, whose values are semantic
 * CSS variable references. Concrete OKLCH values live in `colorPalettes`.
 * Dark and light implement one shape; a component never branches on theme.
 *
 * This is the only file that may contain raw color literals.
 */

export type ThemeName = "dark" | "light";
export type ThemePreference = ThemeName | "system";

type SurfaceRamp = Record<
    "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "10" | "11" | "12",
    string
>;

export interface ColorTokens {
    readonly bg: {
        /** Page background. */
        readonly canvas: string;
        /** First tonal step above the canvas: cards, rows, rails. */
        readonly surface: string;
        /** Second tonal step: secondary controls, nested panels. */
        readonly surface2: string;
        /** Third tonal step: hover state of `surface2` controls. */
        readonly surface3: string;
        /** Selected row or item: accent tint. */
        readonly selected: string;
        /** Hover on a neutral surface: foreground tint. */
        readonly hover: string;
    };
    readonly fg: {
        readonly default: string;
        readonly muted: string;
        readonly subtle: string;
        /** Alias of `default`. */
        readonly primary: string;
        /** Alias of `muted`. */
        readonly secondary: string;
    };
    readonly accent: {
        readonly primary: string;
        readonly primaryHover: string;
        readonly primaryActive: string;
        /** Text and glyphs on an accent fill. */
        readonly onPrimary: string;
        readonly success: string;
        readonly warning: string;
        readonly danger: string;
        readonly info: string;
    };
    /**
     * Hairlines are the foreground at low alpha, so one token reads the same
     * over canvas, surface, and note tints. Nothing at rest carries accent.
     */
    readonly border: {
        /** Resting container edge and divider. */
        readonly subtle: string;
        /** Control edge, scrollbar thumb. */
        readonly default: string;
        /** Drag handles, emphasized separators. */
        readonly strong: string;
        /** Keyboard focus ring on controls: accent at ~70%. */
        readonly focus: string;
        /** Focused text-field edge: accent at ~55%. */
        readonly inputFocus: string;
        /** 3px halo around a focused text field: accent at ~14%. */
        readonly halo: string;
        /** Accent edge for an explicitly highlighted element. Not a resting border. */
        readonly accent: string;
    };
    /** Status pairs. `*Fg` is the glyph or text color; `*Bg` is the soft tint. */
    readonly status: {
        readonly successFg: string;
        readonly successBg: string;
        readonly warningFg: string;
        readonly warningBg: string;
        readonly dangerFg: string;
        readonly dangerBg: string;
        readonly infoFg: string;
        readonly infoBg: string;
    };
    readonly surface: SurfaceRamp;
    /** Gradients are reserved for the page canvas and the brand mark. */
    readonly gradient: {
        readonly canvas: string;
        readonly brandMark: string;
    };
    readonly note: {
        readonly surface: string;
        readonly surfaceRisen: string;
        readonly accent: string;
        readonly border: string;
    };
    readonly effect: {
        /** Base color for every shadow in `elevation`. */
        readonly shadow: string;
        /** Overlay backdrop. */
        readonly scrim: string;
        readonly scrimHidden: string;
        /** Flat avatar fill keyed by `--avatar-hue`. */
        readonly avatarFallback: string;
        /** Identity ring around a present person's avatar, keyed by `--avatar-hue`. */
        readonly presenceRing: string;
        /** Alias of `accent.onPrimary`. */
        readonly onAccent: string;
    };
}

const tint = (base: string, percent: number): string =>
    `color-mix(in oklch, ${base} ${String(percent)}%, transparent)`;

/** `oklch(L C H)` → `oklch(L C H / a)`. Keeps the literal parseable as OKLCH. */
const alpha = (oklch: string, a: number): string => oklch.replace(/\)$/, ` / ${String(a)})`);

// ---------------------------------------------------------------------------
// Dark
// ---------------------------------------------------------------------------

const darkFg = {
    default: "oklch(0.965 0.003 252)",
    muted: "oklch(0.715 0.008 252)",
    subtle: "oklch(0.520 0.010 262)",
} as const;

const darkAccentBase = {
    primary: "oklch(0.650 0.165 250)",
    primaryHover: "oklch(0.700 0.180 250)",
    primaryActive: "oklch(0.580 0.175 250)",
    success: "oklch(0.660 0.170 152)",
    warning: "oklch(0.760 0.160 78)",
    danger: "oklch(0.640 0.200 28)",
    info: "oklch(0.720 0.120 230)",
} as const;

const darkBg = {
    canvas: "oklch(0.135 0.012 262)",
    surface: "oklch(0.185 0.010 252)",
    surface2: "oklch(0.220 0.009 252)",
    surface3: "oklch(0.255 0.008 252)",
    selected: tint(darkAccentBase.primary, 14),
    hover: tint(darkFg.default, 6),
} as const;

const darkOnPrimary = darkBg.canvas;

const darkAccent = {
    ...darkAccentBase,
    onPrimary: darkOnPrimary,
} as const;

const darkBorder = {
    subtle: alpha(darkFg.default, 0.09),
    default: alpha(darkFg.default, 0.15),
    strong: alpha(darkFg.default, 0.26),
    focus: alpha("oklch(0.700 0.170 250)", 0.7),
    inputFocus: alpha("oklch(0.700 0.170 250)", 0.55),
    halo: alpha("oklch(0.700 0.170 250)", 0.14),
    accent: darkAccentBase.primary,
} as const;

const darkStatus = {
    successFg: darkAccentBase.success,
    successBg: tint(darkAccentBase.success, 14),
    warningFg: darkAccentBase.warning,
    warningBg: tint(darkAccentBase.warning, 14),
    dangerFg: darkAccentBase.danger,
    dangerBg: tint(darkAccentBase.danger, 14),
    infoFg: darkAccentBase.info,
    infoBg: tint(darkAccentBase.info, 14),
} as const;

const darkSurface = {
    "0": "oklch(0.105 0.014 262)",
    "1": "oklch(0.135 0.012 262)",
    "2": "oklch(0.165 0.011 262)",
    "3": "oklch(0.190 0.010 252)",
    "4": "oklch(0.220 0.009 252)",
    "5": "oklch(0.250 0.008 252)",
    "6": "oklch(0.290 0.008 252)",
    "7": "oklch(0.335 0.008 252)",
    "8": "oklch(0.385 0.008 252)",
    "9": "oklch(0.445 0.008 252)",
    "10": "oklch(0.515 0.007 252)",
    "11": "oklch(0.620 0.006 252)",
    "12": "oklch(0.760 0.005 252)",
} as const satisfies SurfaceRamp;

const darkNoteSurface = {
    base: "oklch(0.195 0.011 248)",
    risen: "oklch(0.225 0.010 248)",
} as const;

// ---------------------------------------------------------------------------
// Light
// ---------------------------------------------------------------------------

const lightFg = {
    default: "oklch(0.185 0.018 252)",
    muted: "oklch(0.385 0.022 252)",
    subtle: "oklch(0.535 0.018 252)",
} as const;

const lightAccentBase = {
    primary: "oklch(0.460 0.118 250)",
    primaryHover: "oklch(0.420 0.126 250)",
    primaryActive: "oklch(0.360 0.110 250)",
    success: "oklch(0.485 0.145 152)",
    warning: "oklch(0.610 0.145 78)",
    danger: "oklch(0.535 0.195 28)",
    info: "oklch(0.480 0.110 230)",
} as const;

const lightBg = {
    canvas: "oklch(0.955 0.010 252)",
    surface: "oklch(0.985 0.006 252)",
    surface2: "oklch(0.965 0.008 252)",
    surface3: "oklch(0.940 0.010 252)",
    selected: tint(lightAccentBase.primary, 10),
    hover: tint(lightFg.default, 6),
} as const;

const lightOnPrimary = "oklch(0.990 0.003 252)";

const lightAccent = {
    ...lightAccentBase,
    onPrimary: lightOnPrimary,
} as const;

const lightBorder = {
    subtle: alpha(lightFg.default, 0.11),
    default: alpha(lightFg.default, 0.17),
    strong: alpha(lightFg.default, 0.28),
    focus: alpha(lightAccentBase.primary, 0.7),
    inputFocus: alpha(lightAccentBase.primary, 0.55),
    halo: alpha(lightAccentBase.primary, 0.14),
    accent: lightAccentBase.primary,
} as const;

const lightStatus = {
    successFg: lightAccentBase.success,
    successBg: tint(lightAccentBase.success, 12),
    warningFg: lightAccentBase.warning,
    warningBg: tint(lightAccentBase.warning, 12),
    dangerFg: lightAccentBase.danger,
    dangerBg: tint(lightAccentBase.danger, 12),
    infoFg: lightAccentBase.info,
    infoBg: tint(lightAccentBase.info, 12),
} as const;

const lightSurface = {
    "0": "oklch(0.930 0.012 252)",
    "1": lightBg.canvas,
    "2": "oklch(0.970 0.008 252)",
    "3": lightBg.surface,
    "4": "oklch(0.998 0.002 252)",
    "5": "oklch(0.970 0.008 252)",
    "6": "oklch(0.940 0.010 252)",
    "7": "oklch(0.900 0.012 252)",
    "8": "oklch(0.850 0.014 252)",
    "9": "oklch(0.790 0.016 252)",
    "10": "oklch(0.700 0.018 252)",
    "11": "oklch(0.580 0.020 252)",
    "12": "oklch(0.440 0.022 252)",
} as const satisfies SurfaceRamp;

const lightNoteSurface = {
    base: "oklch(0.980 0.020 88)",
    risen: "oklch(0.996 0.012 88)",
} as const;

// ---------------------------------------------------------------------------
// Palettes
// ---------------------------------------------------------------------------

export const colorPalettes = {
    dark: {
        bg: darkBg,
        fg: { ...darkFg, primary: darkFg.default, secondary: darkFg.muted },
        accent: darkAccent,
        border: darkBorder,
        status: darkStatus,
        surface: darkSurface,
        gradient: {
            canvas: [
                "radial-gradient(ellipse 1200px 800px at 18% -10%,",
                ` ${tint(darkBg.surface2, 65)},`,
                " transparent 70%),",
                "radial-gradient(ellipse 1000px 700px at 90% 110%,",
                ` ${tint(darkAccentBase.primary, 8)},`,
                " transparent 65%),",
                darkBg.canvas,
            ].join(""),
            brandMark: "linear-gradient(135deg, oklch(0.700 0.165 250), oklch(0.550 0.165 268))",
        },
        note: {
            surface: darkNoteSurface.base,
            surfaceRisen: darkNoteSurface.risen,
            accent: "oklch(0.600 0.090 68)",
            border: "oklch(0.280 0.012 248)",
        },
        effect: {
            shadow: "oklch(0.040 0.012 262)",
            scrim: "rgb(0 0 0 / 0.55)",
            scrimHidden: "rgb(0 0 0 / 0)",
            avatarFallback: "oklch(0.600 0.110 var(--avatar-hue, 232))",
            presenceRing: "oklch(0.600 0.110 var(--avatar-hue, 232))",
            onAccent: darkOnPrimary,
        },
    },
    light: {
        bg: lightBg,
        fg: { ...lightFg, primary: lightFg.default, secondary: lightFg.muted },
        accent: lightAccent,
        border: lightBorder,
        status: lightStatus,
        surface: lightSurface,
        gradient: {
            canvas: [
                "radial-gradient(ellipse 1200px 800px at 18% -10%,",
                ` ${tint(lightAccentBase.primary, 3)},`,
                " transparent 70%),",
                "radial-gradient(ellipse 1000px 700px at 90% 110%,",
                " color-mix(in oklch, oklch(0.840 0.040 88) 7%, transparent),",
                " transparent 65%),",
                lightBg.canvas,
            ].join(""),
            brandMark: "linear-gradient(135deg, oklch(0.520 0.125 250), oklch(0.415 0.115 268))",
        },
        note: {
            surface: lightNoteSurface.base,
            surfaceRisen: lightNoteSurface.risen,
            accent: "oklch(0.560 0.110 72)",
            border: "oklch(0.860 0.030 88)",
        },
        effect: {
            shadow: "oklch(0.440 0.026 252)",
            scrim: "rgb(16 24 40 / 0.38)",
            scrimHidden: "rgb(16 24 40 / 0)",
            avatarFallback: "oklch(0.480 0.120 var(--avatar-hue, 232))",
            presenceRing: "oklch(0.480 0.120 var(--avatar-hue, 232))",
            onAccent: lightOnPrimary,
        },
    },
} as const satisfies Record<ThemeName, ColorTokens>;

// ---------------------------------------------------------------------------
// Semantic references
// ---------------------------------------------------------------------------

function varRef(group: keyof ColorTokens, key: string): string {
    return `var(--color-${String(group)}-${key})`;
}

export const color = {
    bg: {
        canvas: varRef("bg", "canvas"),
        surface: varRef("bg", "surface"),
        surface2: varRef("bg", "surface2"),
        surface3: varRef("bg", "surface3"),
        selected: varRef("bg", "selected"),
        hover: varRef("bg", "hover"),
    },
    fg: {
        default: varRef("fg", "default"),
        muted: varRef("fg", "muted"),
        subtle: varRef("fg", "subtle"),
        primary: varRef("fg", "primary"),
        secondary: varRef("fg", "secondary"),
    },
    accent: {
        primary: varRef("accent", "primary"),
        primaryHover: varRef("accent", "primaryHover"),
        primaryActive: varRef("accent", "primaryActive"),
        onPrimary: varRef("accent", "onPrimary"),
        success: varRef("accent", "success"),
        warning: varRef("accent", "warning"),
        danger: varRef("accent", "danger"),
        info: varRef("accent", "info"),
    },
    border: {
        subtle: varRef("border", "subtle"),
        default: varRef("border", "default"),
        strong: varRef("border", "strong"),
        focus: varRef("border", "focus"),
        inputFocus: varRef("border", "inputFocus"),
        halo: varRef("border", "halo"),
        accent: varRef("border", "accent"),
    },
    status: {
        successFg: varRef("status", "successFg"),
        successBg: varRef("status", "successBg"),
        warningFg: varRef("status", "warningFg"),
        warningBg: varRef("status", "warningBg"),
        dangerFg: varRef("status", "dangerFg"),
        dangerBg: varRef("status", "dangerBg"),
        infoFg: varRef("status", "infoFg"),
        infoBg: varRef("status", "infoBg"),
    },
    surface: {
        "0": varRef("surface", "0"),
        "1": varRef("surface", "1"),
        "2": varRef("surface", "2"),
        "3": varRef("surface", "3"),
        "4": varRef("surface", "4"),
        "5": varRef("surface", "5"),
        "6": varRef("surface", "6"),
        "7": varRef("surface", "7"),
        "8": varRef("surface", "8"),
        "9": varRef("surface", "9"),
        "10": varRef("surface", "10"),
        "11": varRef("surface", "11"),
        "12": varRef("surface", "12"),
    },
    gradient: {
        canvas: varRef("gradient", "canvas"),
        brandMark: varRef("gradient", "brandMark"),
    },
    note: {
        surface: varRef("note", "surface"),
        surfaceRisen: varRef("note", "surfaceRisen"),
        accent: varRef("note", "accent"),
        border: varRef("note", "border"),
    },
    effect: {
        shadow: varRef("effect", "shadow"),
        scrim: varRef("effect", "scrim"),
        scrimHidden: varRef("effect", "scrimHidden"),
        avatarFallback: varRef("effect", "avatarFallback"),
        presenceRing: varRef("effect", "presenceRing"),
        onAccent: varRef("effect", "onAccent"),
    },
} as const satisfies ColorTokens;

export type SurfaceStep = keyof typeof color.surface;

export const approvedTextPairs = [
    { fg: "fg.default", bg: "bg.canvas", tier: "body" },
    { fg: "fg.default", bg: "bg.surface", tier: "body" },
    { fg: "fg.default", bg: "surface.5", tier: "body" },
    { fg: "fg.muted", bg: "bg.canvas", tier: "fluent" },
    { fg: "fg.muted", bg: "surface.3", tier: "fluent" },
    { fg: "fg.subtle", bg: "bg.canvas", tier: "secondary" },
    { fg: "fg.subtle", bg: "surface.3", tier: "tertiary" },
    { fg: "accent.onPrimary", bg: "accent.primary", tier: "on-accent" },
    { fg: "accent.onPrimary", bg: "accent.danger", tier: "on-accent" },
    { fg: "accent.onPrimary", bg: "accent.success", tier: "on-accent" },
    { fg: "status.successFg", bg: "status.successBg", tier: "status" },
    { fg: "status.dangerFg", bg: "status.dangerBg", tier: "status" },
] as const;

export const AVATAR_HUES = [
    340, // Rose
    18,  // Coral
    42,  // Amber Ochre
    78,  // Olive Gold
    128, // Emerald
    162, // Teal
    196, // Cerulean
    232, // Cobalt
    268, // Indigo
    298, // Purple
    322, // Plum
    214, // Slate Cyan
] as const;

/** Deterministically maps any string identifier to one of 12 balanced OKLCH avatar hues. */
export function avatarHueFromKey(key: string): number {
    if (!key) return AVATAR_HUES[0];
    let hash = 0;
    for (let i = 0; i < key.length; i += 1) {
        hash = (hash * 31 + key.charCodeAt(i)) | 0;
    }
    const index = Math.abs(hash) % AVATAR_HUES.length;
    return AVATAR_HUES[index] ?? AVATAR_HUES[0];
}

